import {
  BulkActionContainerCol,
  Button,
  Col,
  Table,
  TableActionBarRow,
  DataMappingIcon,
  ValueOrFallback,
} from "melco-ui";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  BulkActionCount,
  FormattedDate,
  EfsMelcoEfsModelsUserFormatListItem,
  Search,
  useTableSelection,
  TableCellLink,
} from "melco-shared-logic";
import { useInputFormats } from "../../hooks/inputFormats/useInputFormats";
import { BulkActionDeleteInputFormats } from "./BulkActionDeleteInputFormats";

const DETAIL_VIEW_PATH = "/input-formats";

export const InputFormatsList: React.FC = () => {
  const {
    isLoading,
    isFetching,
    data,
    isPreviousData,
    errorComponent,
    onTableConfigChange,
    pagination,
    searchQueryChange,
    refetch,
  } = useInputFormats();

  const navigate = useNavigate();

  const { t } = useTranslation();

  const { selectedRowKeys, setSelectedRowKeys, rowSelection, onRow } =
    useTableSelection((row: EfsMelcoEfsModelsUserFormatListItem) => {
      navigate([DETAIL_VIEW_PATH, row.id].join("/"));
    });

  const columns = [
    {
      title: t("input_formats_list.columns.display_name"),
      key: "display_name",
      dataIndex: "display_name",
      sorter: true,
      render: (_: string, item: EfsMelcoEfsModelsUserFormatListItem) => (
        <ValueOrFallback
          value={item.display_name}
          render={(value) => (
            <TableCellLink to={[DETAIL_VIEW_PATH, item.id].join("/")}>
              {value}
            </TableCellLink>
          )}
          fallback={t("global.fallback.empty")}
        />
      ),
    },
    {
      title: t("input_formats_list.columns.type"),
      key: "key",
      dataIndex: "key",
      sorter: true,
      render: (_: string, item: EfsMelcoEfsModelsUserFormatListItem) => (
        <ValueOrFallback
          value={item.key && t(`inputFormats.types.${item.key.toLowerCase()}`)}
          fallback={t("global.fallback.empty")}
        />
      ),
    },
    {
      title: t("input_formats_list.columns.config_count"),
      key: "config_count",
      dataIndex: "config_count",
      sorter: true,
      render: (configCount: string) => (
        <ValueOrFallback
          value={configCount}
          fallback={t("global.fallback.empty")}
        />
      ),
    },
    {
      title: t("input_formats_list.columns.created_date"),
      key: "created_date",
      dataIndex: "created_date",
      sorter: true,
      render: (_: string, item: EfsMelcoEfsModelsUserFormatListItem) => (
        <FormattedDate date={item.created_date} />
      ),
    },
    {
      title: t("input_formats_list.columns.updated_date"),
      key: "updated_date",
      dataIndex: "updated_date",
      sorter: true,
      render: (_: string, item: EfsMelcoEfsModelsUserFormatListItem) => (
        <FormattedDate date={item.updated_date} />
      ),
    },
    {
      title: t("input_formats_list.columns.actions"),
      key: "actions",
      dataIndex: "actions",
      render: (_: string, item: EfsMelcoEfsModelsUserFormatListItem) => {
        const targetLink = `/input-formats/${item.id!}/data-mappings`;

        return (
          <Button
            icon={<DataMappingIcon />}
            size="small"
            href={targetLink}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              navigate(targetLink);
            }}
          >
            {t("dataMappings.actions.manage.button")}
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <TableActionBarRow>
        <Col>
          <Search
            onSearchInputChange={(query: string) => searchQueryChange(query)}
          />
        </Col>
        <BulkActionContainerCol>
          <BulkActionCount selectedCount={selectedRowKeys.length} />
          <BulkActionDeleteInputFormats
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
            refetch={refetch}
          />
        </BulkActionContainerCol>
      </TableActionBarRow>
      <Table
        showSorterTooltip={false}
        sticky
        loading={isPreviousData ? isFetching : isLoading}
        dataSource={data}
        columns={columns}
        onChange={onTableConfigChange}
        pagination={pagination}
        rowKey="id"
        rowSelection={rowSelection}
        onRow={onRow}
        rowClassName={() => "clickable"}
        noDataMessage={t("input_formats_list.empty")}
        errorComponent={errorComponent}
      />
    </>
  );
};
